import { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import AICollapse from '../../ai-tools/AICollapse';
import AITile from '../../ai-tools/AITile';
import Toast from '../../../../atoms/Toast';
import api from '../../../../../utils/api';
import { API_ENDPOINTS } from '../../../../../config';
import '../../../../../styles/kanbans/ai-tools/ai-tools-tab.css';

interface AIToolsTabProps {
  jobId?: string;
}

interface AICounter {
  status: 'created' | 'launched' | 'completed' | 'failed';
  count: number;
}

interface AITaskStatus {
  type: string;
  counters: AICounter[];
}

const TASK_TYPE_TO_TITLE: Record<string, string> = {
  'cover letter': 'Cover Letter',
  'follow up after application': 'Follow Up After Application',
  'interview prep questions': 'Interview Prep Questions',
  'questions to ask': 'Questions to Ask',
  'thank you after interview': 'Thank You After Interview',
  'follow up after interview': 'Follow Up After Interview',
  'reschedule interview': 'Reschedule Interview',
  'decline interview': 'Decline Interview',
  'offer negotiation': 'Offer Negotiation',
  'offer acceptance': 'Offer Acceptance',
  'offer decline': 'Offer Decline',
  'offer time extensions request': 'Offer Time Extension Request',
};

export default function AIToolsTab({ jobId }: AIToolsTabProps) {
  const [loadingTile, setLoadingTile] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [taskStatuses, setTaskStatuses] = useState<Record<string, AICounter[]>>({});

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [highlightedTool, setHighlightedTool] = useState<string | null>(
    () => searchParams.get('tool'),
  );

  useEffect(() => {
    if (!highlightedTool) return;

    const scrollTimer = window.setTimeout(() => {
      const el = document.querySelector(
        `[data-task-type="${highlightedTool}"]`,
      ) as HTMLElement | null;
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 200);

    const clearTimer = window.setTimeout(() => {
      setHighlightedTool(null);
      const next = new URLSearchParams(searchParams);
      next.delete('tool');
      const query = next.toString();
      navigate(`${location.pathname}${query ? `?${query}` : ''}`, { replace: true });
    }, 6000);

    return () => {
      window.clearTimeout(scrollTimer);
      window.clearTimeout(clearTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightedTool]);

  useEffect(() => {
    const fetchTaskStatuses = async () => {
      if (!jobId) return;

      try {
        const response = await api.get<AITaskStatus[]>(API_ENDPOINTS.aiTasks.getByStatuses(jobId));
        
        const statusesMap: Record<string, AICounter[]> = {};
        response.forEach((taskStatus) => {
          statusesMap[taskStatus.type] = taskStatus.counters;
        });
        
        setTaskStatuses(statusesMap);
      } catch (error) {

      }
    };

    fetchTaskStatuses();
  }, [jobId]);

  const hasPendingTasks = useMemo(
    () =>
      Object.values(taskStatuses).some(counters =>
        counters?.some(c => (c.status === 'launched' || c.status === 'created') && c.count > 0),
      ),
    [taskStatuses],
  );

  const completedCountRef = useRef(0);
  useEffect(() => {
    let total = 0;
    Object.values(taskStatuses).forEach(counters => {
      counters?.forEach(c => {
        if (c.status === 'completed') total += c.count;
      });
    });
    completedCountRef.current = total;
  }, [taskStatuses]);

  useEffect(() => {
    if (!jobId || !hasPendingTasks) return;

    const MAX_ATTEMPTS = 60;
    let attempts = 0;

    const interval = window.setInterval(async () => {
      attempts += 1;
      if (attempts > MAX_ATTEMPTS) {
        window.clearInterval(interval);
        return;
      }
      try {
        const response = await api.get<AITaskStatus[]>(
          API_ENDPOINTS.aiTasks.getByStatuses(jobId),
        );
        const next: Record<string, AICounter[]> = {};
        let newCompletedCount = 0;
        response.forEach(ts => {
          next[ts.type] = ts.counters;
          ts.counters.forEach(c => {
            if (c.status === 'completed') newCompletedCount += c.count;
          });
        });

        const prevCompleted = completedCountRef.current;
        setTaskStatuses(next);

        if (newCompletedCount > prevCompleted) {
          window.dispatchEvent(
            new CustomEvent('ai-document-created', { detail: { jobId } }),
          );
        }
      } catch {
      }
    }, 4000);

    return () => window.clearInterval(interval);
  }, [jobId, hasPendingTasks]);

  const handleCreateAITask = async (taskType: string, taskName: string) => {
    if (!jobId) {
      setToast({ message: 'Job ID is required', type: 'error' });
      return;
    }

    setLoadingTile(taskType);

    try {
      const response = await api.post(API_ENDPOINTS.aiTasks.create, {
        task_type: taskType,
        job_uid: jobId,
      });

      if (response) {
        setToast({ 
          message: `${taskName} task created successfully!`, 
          type: 'success' 
        });
        
        if (jobId) {
          try {
            const statusResponse = await api.get<AITaskStatus[]>(API_ENDPOINTS.aiTasks.getByStatuses(jobId));
            const statusesMap: Record<string, AICounter[]> = {};
            statusResponse.forEach((taskStatus) => {
              statusesMap[taskStatus.type] = taskStatus.counters;
            });
            setTaskStatuses(statusesMap);
          } catch (error) {

          }
        }
      }
    } catch (error: any) {

      setToast({ 
        message: error?.message || 'Failed to create AI task. Please try again.', 
        type: 'error' 
      });
    } finally {
      setLoadingTile(null);
    }
  };
  return (
    <div className="ai-tools-tab">
      <AICollapse title="Application Stage" defaultExpanded={true}>
        <div className="ai-collapse__tiles">
          <AITile
            title="Cover Letter"
            description="A personalized letter to employers, explaining why you are good fit for the position and how your skills and experiences align with the company's needs."
            actionLabel="Generate Cover Letter"
            isLoading={loadingTile === 'cover letter'}
            onActionClick={() => handleCreateAITask('cover letter', 'Cover Letter')}
            statusCounters={taskStatuses['cover letter']}
            taskType="cover letter"
            isHighlighted={highlightedTool === 'cover letter'}
          />
          <AITile
            title="Follow Up After Application"
            description="If there's no response from the employer within a reasonable timeframe, a polite follow-up email can be sent to inquire about the status of the application."
            actionLabel="Generate Follow Up"
            isLoading={loadingTile === 'follow up after application'}
            onActionClick={() => handleCreateAITask('follow up after application', 'Follow Up After Application')}
            statusCounters={taskStatuses['follow up after application']}
            taskType="follow up after application"
            isHighlighted={highlightedTool === 'follow up after application'}
          />
        </div>
      </AICollapse>

      <AICollapse title="Interview Stage" defaultExpanded={true}>
        <div className="ai-collapse__tiles">
          <AITile
            title="Interview Prep Questions"
            description="A list of questions to help you prepare for an upcoming interview."
            actionLabel="Generate Interview Prep"
            isLoading={loadingTile === 'interview prep questions'}
            onActionClick={() => handleCreateAITask('interview prep questions', 'Interview Prep Questions')}
            statusCounters={taskStatuses['interview prep questions']}
            taskType="interview prep questions"
            isHighlighted={highlightedTool === 'interview prep questions'}
          />
          <AITile
            title="Questions to Ask"
            description="Generate a curated list of questions for job interviewees to ask employers, aimed at gaining deeper insights into the role and company culture."
            actionLabel="Generate Questions"
            isLoading={loadingTile === 'questions to ask'}
            onActionClick={() => handleCreateAITask('questions to ask', 'Questions to Ask')}
            statusCounters={taskStatuses['questions to ask']}
            taskType="questions to ask"
            isHighlighted={highlightedTool === 'questions to ask'}
          />
          <AITile
            title="Thank You After Interview"
            description="Sent shortly after an interview, this message expresses gratitude for the interview, reinforces your interest in the position, and briefly restates why you're a good fit."
            actionLabel="Generate Thank You"
            isLoading={loadingTile === 'thank you after interview'}
            onActionClick={() => handleCreateAITask('thank you after interview', 'Thank You After Interview')}
            statusCounters={taskStatuses['thank you after interview']}
            taskType="thank you after interview"
            isHighlighted={highlightedTool === 'thank you after interview'}
          />
          <AITile
            title="Follow Up After Interview"
            description="If there hasn't been a response within the expected timeframe, this email is sent to inquire about the status of the application and to reiterate interest in the position."
            actionLabel="Generate Follow Up"
            isLoading={loadingTile === 'follow up after interview'}
            onActionClick={() => handleCreateAITask('follow up after interview', 'Follow Up After Interview')}
            statusCounters={taskStatuses['follow up after interview']}
            taskType="follow up after interview"
            isHighlighted={highlightedTool === 'follow up after interview'}
          />
          <AITile
            title="Reschedule Interview"
            description="In case of unforeseen circumstances that require rescheduling an interview, this email is used to request a new date and time."
            actionLabel="Generate Reschedule"
            isLoading={loadingTile === 'reschedule interview'}
            onActionClick={() => handleCreateAITask('reschedule interview', 'Reschedule Interview')}
            statusCounters={taskStatuses['reschedule interview']}
            taskType="reschedule interview"
            isHighlighted={highlightedTool === 'reschedule interview'}
          />
          <AITile
            title="Decline Interview"
            description="An email to decline an interview, expressing your apologies and providing a brief explanation for your decision."
            actionLabel="Generate Decline"
            isLoading={loadingTile === 'decline interview'}
            onActionClick={() => handleCreateAITask('decline interview', 'Decline Interview')}
            statusCounters={taskStatuses['decline interview']}
            taskType="decline interview"
            isHighlighted={highlightedTool === 'decline interview'}
          />
        </div>
      </AICollapse>

      <AICollapse title="Offer Stage" defaultExpanded={true}>
        <div className="ai-collapse__tiles">
          <AITile
            title="Offer Negotiation"
            description="An email to negotiate the terms of a job offer, including salary, benefits, and other terms."
            actionLabel="Generate Negotiation"
            isLoading={loadingTile === 'offer negotiation'}
            onActionClick={() => handleCreateAITask('offer negotiation', 'Offer Negotiation')}
            statusCounters={taskStatuses['offer negotiation']}
            taskType="offer negotiation"
            isHighlighted={highlightedTool === 'offer negotiation'}
          />
          <AITile
            title="Offer Acceptance"
            description="A message confirming the acceptance of the job offer. It usually includes gratitude for the opportunity, confirmation of the terms, and enthusiasm."
            actionLabel="Generate Acceptance"
            isLoading={loadingTile === 'offer acceptance'}
            onActionClick={() => handleCreateAITask('offer acceptance', 'Offer Acceptance')}
            statusCounters={taskStatuses['offer acceptance']}
            taskType="offer acceptance"
            isHighlighted={highlightedTool === 'offer acceptance'}
          />
          <AITile
            title="Offer Decline"
            description="An email to decline a job offer, expressing your gratitude and providing a brief explanation for your decision."
            actionLabel="Generate Decline"
            isLoading={loadingTile === 'offer decline'}
            onActionClick={() => handleCreateAITask('offer decline', 'Offer Decline')}
            statusCounters={taskStatuses['offer decline']}
            taskType="offer decline"
            isHighlighted={highlightedTool === 'offer decline'}
          />
          <AITile
            title="Offer Time Extension Request"
            description="A message asking for more time to consider a job offer. It typically includes valid reasons for the request and expresses continued interest in the position."
            actionLabel="Generate Extension Request"
            isLoading={loadingTile === 'offer time extensions request'}
            onActionClick={() => handleCreateAITask('offer time extensions request', 'Offer Time Extension Request')}
            statusCounters={taskStatuses['offer time extensions request']}
            taskType="offer time extensions request"
            isHighlighted={highlightedTool === 'offer time extensions request'}
          />
        </div>
      </AICollapse>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

